/*
 * Copyright (c) CIRROM 2021.
 */

import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  approveSuggestedChange,
  getCRMURLForSuggestedChange,
  getExplainForSuggestedChange,
  transitionSuggestedChange,
} from "../../api/suggestedchanges";
import Button from "@mui/material/Button";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import IconButton from "@mui/material/IconButton";
import TableRow from "@mui/material/TableRow";
import { red } from "@mui/material/colors";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import Modal from "@mui/material/Modal";
import Card from "@mui/material/Card";
import Container from "@mui/material/Container";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import { green, orange } from "@mui/material/colors";
import Collapse from "@mui/material/Collapse";
import Alert, { AlertColor } from "@mui/material/Alert";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import DomainIcon from "@mui/icons-material/Domain";
import PublicIcon from "@mui/icons-material/Public";
import FolderIcon from "@mui/icons-material/Folder";
import Tooltip from "@mui/material/Tooltip";
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import GppBadIcon from "@mui/icons-material/GppBad";
import Zoom from "@mui/material/Zoom";
import LinearProgress from "@mui/material/LinearProgress";
import NavigateNext from "@mui/icons-material/NavigateNext";
import NavigateBefore from "@mui/icons-material/NavigateBefore";
import { TableRowForSuggestedChange } from "./TableRowForSuggestedChange";
import { encodeValue, toReadableText } from "../utils/utils";
import { buildLinkedInSearchUrl } from "../../utils/linkedinUtils";

import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import {
  FeedbackOutlined,
  Report,
  Send,
  Share,
  ContentCopy,
} from "@mui/icons-material";
import {
  CirromMonitorCRMComparison,
  getMonitorCRMComparison,
} from "../../api/cirrommonitor";
import { askCopilot } from "../../api/llms";
import { MonitorComparisonTable } from "../cirrom-monitor/MonitorComparisonTable";
import Link from "@mui/material/Link";
import {
  UserProfileContext,
  SideBarWidthContext,
  ViewPortContext,
} from "../../App";
import TextField from "@mui/material/TextField";
import { submitFeedback } from "../../api/useractions";
import { CirromLogoAvatar } from "../Logo";
import { toPng } from "html-to-image";
import { isEmpty } from "lodash";

/**
 * Represents a component for displaying a loading state while fetching record details.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {Function} props.onClick - The function to be called when the close button is clicked.
 *
 * @returns {JSX.Element} The rendered React component.
 */
const RecordDetailsNotReady = (props: { onClick: () => void }) => {
  return (
    <Zoom in={true} style={{ transitionDelay: "250ms" }}>
      <Card>
        <CardHeader
          style={{ padding: "20px" }}
          title={
            "Fetching up to date customer data and decoding local information"
          }
          titleTypographyProps={{ variant: "h4" }}
          avatar={<CirromLogoAvatar />}
          action={
            <IconButton onClick={props.onClick}>
              <CloseOutlinedIcon />
            </IconButton>
          }
        />
        <LinearProgress />
        <div style={{ textAlign: "center" }}>
          <img
            src={"/undraw_security.svg"}
            style={{ height: "256px", padding: "20px" }}
            alt={"fetching-records-securely"}
          />
        </div>
      </Card>
    </Zoom>
  );
};

/**
 * Represents a component that allows the user to provide feedback and submit it.
 * @param {Object} props - The properties for the component.
 * @param {Function} props.onClose - The callback function to be called when the component is closed.
 * @returns {JSX.Element} - The JSX element representing the component.
 */
const RecordDetailsFeedback = (props: { onClose: () => void }) => {
  const [feedbackText, setFeedbackText] = useState<string>("");

  return (
    <div
      style={{
        padding: "5px",
        margin: "5px",
        border: "1px dashed",
        display: "flex",
        alignItems: "end",
        gap: "5px",
      }}
    >
      <TextField
        fullWidth
        multiline
        rows={2}
        placeholder={
          "NOTE: No PII data will be included with your submission - please think carefully what data you choose to share."
        }
        inputProps={{
          style: {
            fontSize: "12px",
            lineHeight: "12px",
            padding: "0px",
          },
        }}
        value={feedbackText}
        onChange={(event) => setFeedbackText(event.target.value)}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "end",
          gap: "5px",
        }}
      >
        <IconButton
          size="small"
          onClick={() => props.onClose()}
          color={"error"}
          style={{ border: "2px solid rgb(215 101 93)" }}
        >
          <CloseIcon fontSize={"small"} />
        </IconButton>
        <IconButton
          size="small"
          color={"primary"}
          style={{ border: "2px solid rgb(255 191 35)" }}
          onClick={() =>
            submitFeedback(feedbackText).then((response) => {
              setFeedbackText("");
              props.onClose();
            })
          }
          disabled={!feedbackText}
        >
          <Send fontSize={"small"} />
        </IconButton>
      </div>
    </div>
  );
};

/**
 * Render a traffic light component based on the provided variant and status.
 *
 * @param {Object} props - The props object containing the variant and status.
 * @param {string} props.variant - The variant of the traffic light component (success, warning, error).
 * @param {string} props.status - The status message to be displayed.
 *
 * @return {JSX.Element} - The rendered traffic light component.
 */
export const EmailTrafficLight = (props: {
  variant: string;
  status: string;
}) => {
  return (
    <>
      {props.variant && props.status && props.variant === "success" && (
        <div style={{ display: "flex" }}>
          <GppGoodIcon style={{ color: green["900"], height: "21px" }} />
          <Typography variant={"caption"}>{props.status}</Typography>
        </div>
      )}
      {props.variant && props.status && props.variant === "warning" && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <GppMaybeIcon style={{ color: orange["A400"], height: "21px" }} />
          <Typography variant={"caption"}>{props.status}</Typography>
        </div>
      )}
      {props.variant && props.status && props.variant === "error" && (
        <div style={{ display: "flex", alignItems: "center" }}>
          <GppBadIcon style={{ color: red["A400"], height: "21px" }} />
          <Typography variant={"caption"}>{props.status}</Typography>
        </div>
      )}
    </>
  );
};

interface CopilotMessage {
  role: "user" | "assistant";
  content: string;
}

const CopilotPanel = (props: {
  integratedSystemId: number;
  recordId?: number;
  suggestedChangeId?: number;
  onClose: () => void;
}) => {
  const [messages, setMessages] = useState<CopilotMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendQuery = (question?: string, predefinedQuestion?: string) => {
    if (isLoading) return;
    const displayText = question || predefinedQuestion;
    if (!displayText) return;

    setMessages((prev) => [...prev, { role: "user", content: displayText }]);
    setInput("");
    setIsLoading(true);

    askCopilot({
      integrated_system_id: props.integratedSystemId,
      record_id: props.recordId,
      suggested_change_id: props.suggestedChangeId,
      question: question || undefined,
      predefined_question: predefinedQuestion || undefined,
    })
      .then((response) => {
        if (!mountedRef.current) return;
        const answer =
          response?.answer ||
          response?.raw_query ||
          (response?.records?.length
            ? `Found ${response.records.length} record(s).`
            : "No results found.");
        setMessages((prev) => [
          ...prev,
          { role: "assistant", content: answer },
        ]);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, I was unable to process that request.",
          },
        ]);
      })
      .finally(() => {
        if (mountedRef.current) setIsLoading(false);
      });
  };

  const handleSend = () => {
    if (!input.trim()) return;
    sendQuery(input.trim());
  };

  return (
    <Card
      style={{
        width: "350px",
        minWidth: "350px",
        display: "flex",
        flexDirection: "column",
        alignSelf: "stretch",
        overflow: "hidden",
      }}
    >
      <CardHeader
        title="Co-Pilot"
        titleTypographyProps={{ variant: "h5" }}
        avatar={
          <img
            src="/copilot.png"
            alt="copilot"
            style={{ width: "32px", height: "32px", borderRadius: "8px" }}
          />
        }
        action={
          <IconButton onClick={props.onClose} size="small">
            <CloseOutlinedIcon />
          </IconButton>
        }
        style={{ padding: "12px 16px" }}
      />
      <Divider />
      <Container
        style={{
          minHeight: "400px",
          maxHeight: "calc(100vh - 270px)",
          overflowY: "auto",
          height: "100%",
        }}
      >
        <div
          style={{
            flex: 1,
            overflowY: "auto",
            padding: "12px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            minHeight: 0,
          }}
        >
          {messages.length === 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                opacity: 0.5,
              }}
            >
              <img
                src="/copilot.png"
                alt="copilot"
                style={{ width: "48px", height: "48px", marginBottom: "8px" }}
              />
              <Typography variant="caption" style={{ textAlign: "center" }}>
                Ask your Co-Pilot about this record
              </Typography>
            </div>
          )}
          {messages.map((msg, idx) => (
            <div
              key={idx}
              style={{
                display: "flex",
                justifyContent: msg.role === "user" ? "flex-end" : "flex-start",
              }}
            >
              <div
                style={{
                  borderRadius: "10px",
                  padding: "8px 12px",
                  maxWidth: "85%",
                  background: msg.role === "user" ? "#ffbf23" : "#f0f0f0",
                  color: "black",
                }}
              >
                <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                  {msg.content}
                </Typography>
              </div>
            </div>
          ))}
          {isLoading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div
                style={{
                  borderRadius: "10px",
                  padding: "8px 12px",
                  background: "#f0f0f0",
                }}
              >
                <LinearProgress style={{ width: "80px" }} />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </Container>

      <Divider />
      <div
        style={{
          display: "flex",
          gap: "6px",
          padding: "8px 8px 0 8px",
          flexWrap: "wrap",
        }}
      >
        {[
          { label: "Track whereabouts", prompt: "Track whereabouts" },
          { label: "Find internal data", prompt: "Find internal data" },
        ].map((pill) => (
          <Button
            key={pill.label}
            size="small"
            variant="outlined"
            disabled={isLoading}
            onClick={() => {
              sendQuery(undefined, pill.prompt);
            }}
            style={{
              borderRadius: "16px",
              textTransform: "none",
              fontSize: "11px",
              padding: "2px 10px",
              background: "#ffbf23",
              border: "1px solid #e0a800",
              color: "black",
              fontWeight: 600,
            }}
          >
            {pill.label}
          </Button>
        ))}
      </div>
      <div
        style={{
          display: "flex",
          padding: "8px",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <TextField
          fullWidth
          size="small"
          variant="outlined"
          placeholder="Ask a question..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          inputProps={{ style: { fontSize: "13px" } }}
        />
        <IconButton
          onClick={handleSend}
          disabled={!input.trim() || isLoading}
          color="primary"
          size="small"
          style={{
            background: input.trim() ? "#ffbf23" : undefined,
            color: input.trim() ? "black" : undefined,
          }}
        >
          <Send fontSize="small" />
        </IconButton>
      </div>
    </Card>
  );
};

/**
 * SuggestedChangeDetailsModal Component
 *
 * This component displays the details of a suggested change in a modal dialog.
 *
 * @param {Object} props - Component props.
 * @param {boolean} props.open - Determines if the modal is open or closed.
 * @param {function} [props.onClose] - Callback function to handle modal closure.
 * @param {RowNode|any} props.selectedNode - Selected row node or data.
 * @param {function} props.onSuccess - Callback function when a change is successfully approved.
 * @param {function} [props.onNextRowRequested] - Callback function to navigate to the next row.
 * @returns {JSX.Element} - SuggestedChangeDetailsModal component JSX element.
 */
export function SuggestedChangeDetailsModal(
  props: Readonly<{
    open: boolean;
    onClose?: () => void;
    onSuccess: (id?) => void;
    onPreviousRowRequested?: () => void;
    onNextRowRequested?: () => void;
    suggestedChangeId?: number;
    recordId?: number;
    batchId: number;
    integratedSystemId?: number;
  }>
) {
  const {
    suggestedChangeId,
    batchId,
    recordId,
    onPreviousRowRequested,
    onNextRowRequested,
  } = props;
  // State management
  const [entityType, setEntityType] = useState<string>("");
  const [suggestedExplain, setSuggestedExplain] = useState<any>(undefined);
  const [alertOpen, setAlertOpen] = useState<boolean>(false);
  const [alertText, setAlertText] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("success");
  const [sourceFolderName, setSourceFolderName] = useState<string>(undefined);
  const [contactUrl, setContactUrl] = useState<string>("");
  const [accountUrl, setAccountUrl] = useState<string>("");
  const [companyUrl, setCompanyUrl] = useState<string>(undefined);
  const [emailStatuses, setEmailStatuses] = useState<string[][]>([]);
  const [requestInProgress, setRequestInProgress] = useState<boolean>(false);
  const [duplicateGroupId, setDuplicateGroupId] = useState<number>(null);
  const [integratedSystemId, setIntegratedSystemId] = useState<number>(
    props.integratedSystemId
  );

  const viewPortProps = useContext(ViewPortContext);
  const sideBarContext = useContext(SideBarWidthContext);
  const feedbackContext = useContext(UserProfileContext);

  const [isSideViewOpen, setIsSideViewOpen] = useState<boolean>(false);
  const [reduceContainerMargin, setReduceContainerMargin] =
    useState<boolean>(false);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<number>(0);
  const [monitorData, setMonitorData] =
    useState<CirromMonitorCRMComparison>(undefined);
  const [monitorRequestInProgress, setMonitorRequestInProgress] =
    useState<boolean>(false);

  const [changeRecordId, setChangeRecordId] = useState<number>(null);
  const [changeIntegratedSystemId, setChangeIntegratedSystemId] =
    useState<number>(null);

  const [lastBrowserView, setLastBrowserView] = useState<string>("");
  const [isCopilotOpen, setIsCopilotOpen] = useState<boolean>(false);
  const cardRef = useRef<HTMLDivElement>(null);
  /**
   * generateEmailStatusMessage function
   * Based on the provided response, generate an ( alert_type, alert_message ) tuple
   * This will display banners at the top of the modal indicating the email marketing activity
   * for the given contact
   * @param response
   */

  const generateEmailStatusMessage = (response) => {
    const emailStatuses = [];
    //success messages
    if (
      response?.email_data?.last_email_delivered ||
      response?.email_data?.last_email_open
    ) {
      if (response?.email_data?.last_email_open) {
        emailStatuses.push([
          "success",
          `Last opened email on ${new Date(
            Date.parse(response.email_data.last_email_open)
          ).toDateString()}.`,
        ]);
      } else if (response?.email_data?.last_email_delivered) {
        emailStatuses.push([
          "success",
          `Last delivered email on ${new Date(
            Date.parse(response.email_data.last_email_delivered)
          ).toDateString()}.`,
        ]);
      }
    }
    //errors
    if (
      response?.email_data?.last_email_hard_bounce ||
      response?.email_data?.last_email_soft_bounce
    ) {
      emailStatuses.push([
        "error",
        `Email ${
          response?.email_data?.last_email_soft_bounce ? "soft" : ""
        } bounce event on ${new Date(
          Date.parse(
            response?.email_data?.last_email_hard_bounce ||
              response?.email_data?.last_email_soft_bounce
          )
        ).toDateString()}.`,
      ]);
    }
    //never emailed
    if (emailStatuses.length === 0) {
      emailStatuses.push(["warning", "This contact has never been emailed."]);
    }
    setEmailStatuses(emailStatuses);
  };

  /**
   * fetchSuggestedChangeExplain function
   *
   * Retrieve the data required to populate the modal
   */
  const fetchSuggestedChangeExplain = () => {
    getExplainForSuggestedChange(
      suggestedChangeId,
      batchId,
      recordId,
      props.integratedSystemId
    )
      .then((response) => {
        let formattedResponse = {};
        response.contact_data.forEach((item) => {
          formattedResponse[item.field_name] = item;
        });

        generateEmailStatusMessage(response);
        setSourceFolderName(response.source_folder_name);
        setCompanyUrl(response.company_website);
        setDuplicateGroupId(response.duplicate_group_id);
        setIntegratedSystemId(response.integrated_system_id);

        setSuggestedExplain(formattedResponse);
        setChangeRecordId(response.record_id);
        setChangeIntegratedSystemId(response.integrated_system_id);
        setEntityType(response.entity_type);
        const changeIds = new Set<number>();
        Object.keys(formattedResponse).forEach((item) => {
          formattedResponse[item].cirrom_changes.forEach((changeItem) => {
            changeIds.add(changeItem.suggested_change_id);
          });
        });
        getCRMURLForSuggestedChange(changeIds.values().next().value)
          .then((urlResponse) => {
            setContactUrl(urlResponse?.contact);
            setAccountUrl(urlResponse?.account);
          })
          .catch((err) => console.log(err));
      })
      .catch((err) => console.log(err));
  };

  /**
   * Display an alert on request complete
   * @param textToDisplay string
   * @param alertVariant success|error|warning
   */
  const displayAlert = (textToDisplay: string, alertVariant: string) => {
    setAlertSeverity(alertVariant as AlertColor);
    setAlertText(textToDisplay);
    setAlertOpen(true);
    setTimeout(() => setAlertOpen(false), 2500);
  };

  //reset state and fetch new data from the server
  useEffect(() => {
    setSuggestedExplain(undefined);
    setEntityType(undefined);
    setAlertOpen(false);
    setContactUrl("");
    setAccountUrl("");
    setCompanyUrl("");
    setIsFeedbackOpen(false);
    setRequestInProgress(false);
    setActiveTab(0);
    setMonitorData(undefined);
    setMonitorRequestInProgress(false);
    setIsCopilotOpen(false);
    let cancelled = false;
    if (suggestedChangeId || recordId) {
      fetchSuggestedChangeExplain();
      if (recordId && props.integratedSystemId) {
        setMonitorRequestInProgress(true);
        getMonitorCRMComparison(recordId, props.integratedSystemId)
          .then((resp) => {
            if (!cancelled) setMonitorData(resp);
          })
          .catch(() => {
            if (!cancelled) setMonitorData(undefined);
          })
          .finally(() => {
            if (!cancelled) setMonitorRequestInProgress(false);
          });
      }
    }
    return () => {
      cancelled = true;
    };
  }, [suggestedChangeId, recordId, props.integratedSystemId]);

  useEffect(() => {
    let cancelled = false;
    if (changeRecordId) {
      if (changeIntegratedSystemId) {
        getMonitorCRMComparison(changeRecordId, changeIntegratedSystemId)
          .then((resp) => {
            if (!cancelled) setMonitorData(resp);
          })
          .catch(() => {
            if (!cancelled) setMonitorData(undefined);
          })
          .finally(() => {
            if (!cancelled) setMonitorRequestInProgress(false);
          });
      }
    }
    return () => {
      cancelled = true;
    };
  }, [changeRecordId, recordId, changeIntegratedSystemId]);

  useEffect(() => {
    if (
      lastBrowserView &&
      suggestedExplain &&
      //@ts-ignore
      window?.electronAPI?.openSideView
    ) {
      setIsSideViewOpen(true);
      //@ts-ignore
      window.electronAPI
        //@ts-ignore
        .openSideView(getContactSearchURL(lastBrowserView === "google"));
      //@ts-ignore
      if (window?.electronAPI?.onMessage) {
        //@ts-ignore
        window?.electronAPI?.onMessage(() => {
          setIsSideViewOpen(false);
          setLastBrowserView("");
        });
      }
    }
  }, [suggestedExplain, lastBrowserView]);

  /**
   * approveAllSuggestedChanges functino
   * Loop through all changes associated with this record and approve all of them
   *
   * @param negativeTransition - when true, it rejects the  changes
   */
  const approveAllSuggestedChanges = (negativeTransition) => {
    const changeIds = new Set<number>();
    setRequestInProgress(true);
    Object.keys(suggestedExplain).forEach((item) => {
      suggestedExplain[item].cirrom_changes.forEach((changeItem) => {
        if (
          changeItem.status === "Active" &&
          (negativeTransition || changeItem.cirrom_value)
        ) {
          changeIds.add(changeItem.suggested_change_id);
        }
      });
    });
    if (!changeIds.size) {
      setRequestInProgress(false);
      displayAlert("No approvable Cirrom changes found.", "warning");
      return;
    }
    if (negativeTransition) {
      Promise.all(
        Array.from(changeIds).map((changeId) =>
          transitionSuggestedChange([changeId], true)
        )
      )
        .then((_responses) => {
          fetchSuggestedChangeExplain();
          displayAlert(
            "All Cirrom suggested changes for this record have been cancelled!",
            "success"
          );
          setRequestInProgress(false);
        })
        .catch((err) => console.log(err));
    } else {
      Promise.all(
        Array.from(changeIds).map((changeId) =>
          approveSuggestedChange(changeId)
        )
      )
        .then((_responses) => {
          displayAlert("All values successfully approved!", "success");
          changeIds.forEach((id) => props.onSuccess(id));
          fetchSuggestedChangeExplain();
          setRequestInProgress(false);
        })
        .catch((err) => console.log(err));
    }
  };

  /**
   * canApproveOrReject
   * Check if the suggested change is active
   */
  const canApproveOrReject = () => {
    let found = false;
    Object.keys(suggestedExplain).forEach((item) => {
      suggestedExplain[item].cirrom_changes.forEach((changeItem) => {
        if (changeItem.status === "Active") {
          found = true;
        }
      });
    });
    return found;
  };

  /**
   * getContactSearchURL
   * get google/linkedin search urls
   * @param isGoogle
   */
  const getContactSearchURL = (isGoogle?: boolean) => {
    let firstName = suggestedExplain?.first_name?.crm_value;
    let lastName = suggestedExplain?.last_name?.crm_value;
    let company = suggestedExplain?.company?.crm_value;
    if (
      suggestedExplain?.first_name?.cirrom_changes &&
      suggestedExplain.first_name.cirrom_changes.length > 0
    ) {
      firstName = suggestedExplain.first_name.cirrom_changes[0].cirrom_value;
    }
    if (
      suggestedExplain?.last_name?.cirrom_changes &&
      suggestedExplain.last_name.cirrom_changes.length > 0
    ) {
      lastName = suggestedExplain.last_name.cirrom_changes[0].cirrom_value;
    }

    if (isGoogle) {
      const encodedFirstName = encodeValue(firstName);
      const encodedLastName = encodeValue(lastName);
      const encodedCompany = encodeValue(company);
      return `https://www.google.com/search?q=${encodedFirstName} ${encodedLastName} ${encodedCompany}`;
    }
    return buildLinkedInSearchUrl({ firstName, lastName, company });
  };

  /**
   * getFieldNameOrCirromValue
   * if no crm value for field, return the cirrom change
   * @param field
   */
  const getFieldNameOrCirromValue = (field) => {
    if (field?.crm_value) {
      return field.crm_value;
    }
    if (field?.cirrom_changes.length > 0) {
      return field.cirrom_changes[0].cirrom_value;
    }
    return "";
  };

  const canSearchContact =
    entityType === "CONTACT" &&
    Boolean(
      getFieldNameOrCirromValue(suggestedExplain?.first_name) &&
        getFieldNameOrCirromValue(suggestedExplain?.last_name)
    );

  /**
   * Fetch the rows to be populated in the grid, order is important
   */
  const getRecordRows = () => {
    const companyLevelRows = [
      [
        "Telephone",
        suggestedExplain.telephone,
        suggestedExplain?.phone_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.phone_owner_id?.crm_value,
      ],
      [
        "Street",
        suggestedExplain.street,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "City",
        suggestedExplain.city,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "State",
        suggestedExplain.stateorprovince,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "Zip Code",
        suggestedExplain.zipcode,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
      [
        "Country",
        suggestedExplain.country,
        suggestedExplain?.address_owner_id?.crm_value &&
          suggestedExplain?.crm_account_id?.crm_value ===
            suggestedExplain?.address_owner_id?.crm_value,
      ],
    ];

    if (entityType === "CONTACT") {
      let companyValues = suggestedExplain.company;
      if (
        !suggestedExplain?.company?.crm_value &&
        suggestedExplain?.text_company.crm_value
      ) {
        companyValues = {
          ...suggestedExplain.company,
          isTextCompany: true,
          textCompany: { ...suggestedExplain.text_company },
        };
      }
      return [
        ["Salutation", suggestedExplain.salutation, false],
        ["First Name", suggestedExplain.first_name, false],
        ["Middle Name", suggestedExplain.middle_name, false],
        ["Last Name", suggestedExplain.last_name, false],
        ["Goes By", suggestedExplain.goes_by, false],
        ["Suffix", suggestedExplain.suffix, false],
        ["Company", companyValues, false],
        ["Job Title", suggestedExplain.job_title, false],
        ["Email", suggestedExplain.primary_email, false],
        ["Address Linkage", suggestedExplain.address_linkage, false],

        ...companyLevelRows,
      ];
    } else {
      return [
        ["Company", suggestedExplain.company, false],
        ...companyLevelRows,
      ];
    }
  };

  const openExternalLookup = (isGoogle) => {
    setLastBrowserView((isGoogle && "google") || "linkedin");
    //@ts-ignore
    if (window?.electronAPI?.openSideView) {
      //@ts-ignore
      window.electronAPI
        //@ts-ignore
        .openSideView(getContactSearchURL(isGoogle));
      //@ts-ignore
      if (window?.electronAPI?.onMessage) {
        //@ts-ignore
        window?.electronAPI?.onMessage(() => {
          setIsSideViewOpen(false);
          setLastBrowserView("");
        });
      }
    } else {
      window.open(getContactSearchURL(isGoogle), "_blank");
    }
  };

  /**
   * handleExportAsImage
   * Export the modal content as PNG image
   */
  const handleExportAsImage = useCallback(() => {
    if (cardRef.current === null) {
      return;
    }

    const contactName =
      entityType === "CONTACT"
        ? `${getFieldNameOrCirromValue(
            suggestedExplain.first_name
          )} ${getFieldNameOrCirromValue(suggestedExplain.last_name)}`
        : getFieldNameOrCirromValue(suggestedExplain.company);

    toPng(cardRef.current, { cacheBust: true, backgroundColor: "#f4f6f8" })
      .then((dataUrl) => {
        const link = document.createElement("a");
        link.download = `${contactName
          .replaceAll(/[^a-z0-9]/gi, "_")
          .toLowerCase()}.png`;
        link.href = dataUrl;
        link.click();
        displayAlert("Contact record exported as image!", "success");
      })
      .catch((err) => {
        console.log(err);
        displayAlert("Failed to export image", "error");
      });
  }, [entityType, suggestedExplain, cardRef]);

  /**
   * handleShareRecord
   * Generate and open pre-filled email with contact details and export as image
   */
  const handleShareRecord = () => {
    if (cardRef.current === null) {
      displayAlert("Unable to export image for sharing", "error");
      return;
    }

    const contactName =
      entityType === "CONTACT"
        ? `${getFieldNameOrCirromValue(
            suggestedExplain.first_name
          )} ${getFieldNameOrCirromValue(suggestedExplain.last_name)}`
        : getFieldNameOrCirromValue(suggestedExplain.company);

    const company =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.company)
        : "";

    const jobTitle =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.job_title)
        : "";

    const email =
      entityType === "CONTACT"
        ? getFieldNameOrCirromValue(suggestedExplain.primary_email)
        : "";

    const phone = getFieldNameOrCirromValue(suggestedExplain.telephone);

    // Export image to clipboard and then open email
    toPng(cardRef.current, { cacheBust: true, backgroundColor: "#f4f6f8" })
      .then(async (dataUrl) => {
        try {
          // Convert data URL to blob and copy to clipboard
          const response = await fetch(dataUrl);
          const blob = await response.blob();

          await navigator.clipboard.write([
            new ClipboardItem({
              "image/png": blob,
            }),
          ]);

          // Construct email subject and body
          const subject = encodeURIComponent(`Cirrom Contact: ${contactName}`);

          let body = `Hi there,\n\nI wanted to share this contact record from Cirrom:\n\n`;
          body += `Name: ${contactName}\n`;

          if (company) {
            body += `Company: ${company}\n`;
          }

          if (jobTitle) {
            body += `Job Title: ${jobTitle}\n`;
          }

          if (email) {
            body += `Email: ${email}\n`;
          }

          if (phone) {
            body += `Phone: ${phone}\n`;
          }

          body += `\n--- Detailed Record Data ---\n\n`;
          body += `Please find the detailed contact record attached as an image. The image shows all CRM and Cirrom suggested data for easy reference.\n\n`;
          body += `Note: The contact record image has been copied to your clipboard. Please paste it as an attachment in your email.\n\n`;
          body += `Please let me know if you have any questions or need additional context.\n\n`;
          body += `Best regards`;

          const encodedBody = encodeURIComponent(body);
          const mailtoLink = `mailto:?subject=${subject}&body=${encodedBody}`;

          window.open(mailtoLink, "_blank");
          displayAlert(
            "Contact record copied to clipboard and email opened!",
            "success"
          );
        } catch (clipboardErr) {
          console.error("Failed to copy to clipboard:", clipboardErr);
          displayAlert(
            "Email opened, but failed to copy image to clipboard",
            "warning"
          );

          // Still open the email even if clipboard fails
          const subject = encodeURIComponent(`Cirrom Contact: ${contactName}`);
          let body = `Hi there,\n\nI wanted to share this contact record from Cirrom:\n\n`;
          body += `Name: ${contactName}\n`;
          if (company) body += `Company: ${company}\n`;
          if (jobTitle) body += `Job Title: ${jobTitle}\n`;
          if (email) body += `Email: ${email}\n`;
          if (phone) body += `Phone: ${phone}\n`;
          body += `\n--- Detailed Record Data ---\n\nPlease use the separate copy button to get the contact record image for attachment.\n\nBest regards`;

          const encodedBody = encodeURIComponent(body);
          window.open(
            `mailto:?subject=${subject}&body=${encodedBody}`,
            "_blank"
          );
        }
      })
      .catch((err) => {
        console.log(err);
        displayAlert("Failed to export image for sharing", "error");
      });
  };

  /**
   * handleCopyImageToClipboard
   * Copy contact record as image to clipboard
   */
  const handleCopyImageToClipboard = async () => {
    if (cardRef.current === null) {
      displayAlert("Unable to copy image to clipboard", "error");
      return;
    }

    try {
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        backgroundColor: "#f4f6f8",
      });

      // Convert data URL to blob
      const response = await fetch(dataUrl);
      const blob = await response.blob();

      // Copy to clipboard
      await navigator.clipboard.write([
        new ClipboardItem({
          "image/png": blob,
        }),
      ]);

      displayAlert("Contact record copied to clipboard as image!", "success");
    } catch (err) {
      console.error("Failed to copy image to clipboard:", err);
      displayAlert("Failed to copy image to clipboard", "error");
    }
  };

  useEffect(() => {
    //@ts-ignore
    if (window?.electronAPI?.openSideView) {
      if (isSideViewOpen) {
        setReduceContainerMargin(true);
        viewPortProps.minimizeView();
        sideBarContext.minimize();
      } else {
        setReduceContainerMargin(false);
        viewPortProps.maximizeView();
        sideBarContext.maximize();
      }
    }
  }, [isSideViewOpen]);

  return (
    <Modal
      open={props.open}
      onClose={() => {
        //@ts-ignore
        if (window?.electronAPI?.closeSideView) {
          setIsSideViewOpen(false);
          setLastBrowserView("");
          //@ts-ignore
          window.electronAPI.closeSideView();
        }
        props.onClose();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          display: "flex",
          gap: isCopilotOpen ? "12px" : "0px",
          alignItems: "stretch",
          maxWidth: isCopilotOpen ? "1200px" : undefined,
          minWidth: "70vw",
          transition: "all 0.3s ease",
        }}
      >
        <Container
          maxWidth={suggestedExplain ? "md" : "sm"}
          style={{
            padding: "0px",
            marginLeft: reduceContainerMargin && "10px",
            maxWidth: reduceContainerMargin && "60%",
          }}
        >
          {suggestedExplain ? (
            <Zoom in={true}>
              <Card ref={cardRef}>
                <CardHeader
                  style={{ padding: "20px", marginBottom: "14px" }}
                  title={
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "2rem",
                      }}
                    >
                      {entityType === "CONTACT"
                        ? `${getFieldNameOrCirromValue(
                            suggestedExplain.first_name
                          )} ${getFieldNameOrCirromValue(
                            suggestedExplain.last_name
                          )}`
                        : `${getFieldNameOrCirromValue(
                            suggestedExplain.company
                          )}`}
                      <div
                        style={{
                          display: "flex",
                          gap: "10px",
                          alignItems: "center",
                        }}
                      >
                        {sourceFolderName && (
                          <>
                            <Tooltip title={sourceFolderName}>
                              <FolderIcon
                                style={{
                                  width: "20px",
                                  color: "#d7655d",
                                }}
                              />
                            </Tooltip>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                          </>
                        )}

                        {contactUrl && (
                          <Tooltip title={"View Contact in CRM"}>
                            <LaunchIcon
                              style={{ width: "20px", cursor: "pointer" }}
                              onClick={() => window.open(contactUrl, "_blank")}
                            />
                          </Tooltip>
                        )}

                        {accountUrl && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"View Account in CRM"}>
                              <DomainIcon
                                style={{ width: "20px", cursor: "pointer" }}
                                onClick={() =>
                                  window.open(accountUrl, "_blank")
                                }
                              />
                            </Tooltip>
                          </>
                        )}

                        {companyUrl && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Open Company Website"}>
                              <PublicIcon
                                style={{ width: "20px", cursor: "pointer" }}
                                onClick={() =>
                                  window.open(companyUrl, "_blank")
                                }
                              />
                            </Tooltip>
                          </>
                        )}

                        {canSearchContact && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Search on LinkedIn"}>
                              <img
                                src={"/linkedin-logo.svg"}
                                style={{ width: "18px", cursor: "pointer" }}
                                onClick={() => {
                                  openExternalLookup(false);
                                }}
                                onKeyDown={() =>
                                  window.open(getContactSearchURL(), "_blank")
                                }
                                alt={"linkedin-logo"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}
                        {canSearchContact && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Search on Google"}>
                              <img
                                src={"/google-logo.png"}
                                style={{
                                  width: "18px",
                                  height: "18px",
                                  cursor: "pointer",
                                }}
                                onKeyDown={() =>
                                  window.open(
                                    getContactSearchURL(true),
                                    "_blank"
                                  )
                                }
                                onClick={() => {
                                  openExternalLookup(true);
                                }}
                                alt={"google-logo"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}

                        {!!integratedSystemId && feedbackContext.canUseCopilot && (
                          <>
                            <Divider
                              orientation="vertical"
                              variant="middle"
                              flexItem
                            />
                            <Tooltip title={"Ask Co-Pilot"}>
                              <img
                                src={"/copilot.png"}
                                style={{
                                  width: "20px",
                                  height: "20px",
                                  cursor: "pointer",
                                  borderRadius: "4px",
                                  border: isCopilotOpen
                                    ? "2px solid #ffbf23"
                                    : "none",
                                }}
                                onClick={() =>
                                  setIsCopilotOpen((prev) => !prev)
                                }
                                alt={"copilot"}
                                role={"presentation"}
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    </div>
                  }
                  titleTypographyProps={{ variant: "h3" }}
                  subheader={
                    <div>
                      {entityType === "CONTACT" && (
                        <>
                          <div>
                            {suggestedExplain?.contact_source?.crm_value &&
                              ` Sourced from: ${toReadableText(
                                suggestedExplain.contact_source.crm_value
                              )}`}
                            {suggestedExplain?.contact_source?.crm_value &&
                              suggestedExplain?.job_title?.crm_value &&
                              " | "}
                            {`${suggestedExplain?.job_title?.crm_value || ""}`}
                          </div>
                          <div style={{ display: "flex", flexWrap: "wrap" }}>
                            {emailStatuses.map((status) => (
                              <EmailTrafficLight
                                variant={status[0]}
                                status={status[1]}
                                key={`${status[0]}-${status[1]}`}
                              />
                            ))}
                          </div>

                          {duplicateGroupId !== null &&
                            integratedSystemId !== null && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                <Report
                                  style={{ color: red[900], width: "24px" }}
                                  fontSize={"small"}
                                />
                                <Link
                                  underline={"always"}
                                  color={"#6b778c"}
                                  style={{ cursor: "pointer" }}
                                  onClick={() =>
                                    window.open(
                                      window.location.origin +
                                        `/app/duplicates/${integratedSystemId}?duplicateGroupId=${duplicateGroupId}`
                                    )
                                  }
                                >
                                  <Typography variant={"caption"}>
                                    This contact has a duplicate record
                                    associated.
                                  </Typography>
                                </Link>
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  }
                  avatar={
                    <CirromLogoAvatar
                      styleOverrides={{
                        width: "64px",
                        height: "64px",
                      }}
                    />
                  }
                  action={
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <IconButton
                        onClick={() => {
                          //@ts-ignore
                          if (window?.electronAPI?.closeSideView) {
                            setIsSideViewOpen(false);
                            setLastBrowserView("");
                            //@ts-ignore
                            window.electronAPI.closeSideView();
                          }
                          props.onClose();
                        }}
                        color={"error"}
                      >
                        <CloseOutlinedIcon />
                      </IconButton>
                      {(onPreviousRowRequested || onNextRowRequested) && (
                        <div style={{ display: "flex", gap: "4px" }}>
                          <IconButton
                            onClick={() => onPreviousRowRequested?.()}
                            color={"info"}
                            disabled={!onPreviousRowRequested}
                            aria-label="Previous contact"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "0px",
                            }}
                          >
                            <NavigateBefore />
                            <Typography variant={"caption"}>Prev</Typography>
                          </IconButton>
                          <IconButton
                            onClick={() => onNextRowRequested?.()}
                            color={"info"}
                            disabled={!onNextRowRequested}
                            aria-label="Next contact"
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              padding: "0px",
                            }}
                          >
                            <NavigateNext />
                            <Typography variant={"caption"}>Next</Typography>
                          </IconButton>
                        </div>
                      )}
                    </div>
                  }
                />
                <Collapse in={alertOpen}>
                  <Alert
                    severity={alertSeverity}
                    action={
                      <IconButton
                        aria-label="close"
                        color="inherit"
                        size="small"
                        onClick={() => {
                          setAlertOpen(false);
                        }}
                      >
                        <CloseIcon fontSize="inherit" />
                      </IconButton>
                    }
                    sx={{ mb: 2 }}
                  >
                    {alertText}
                  </Alert>
                </Collapse>
                {requestInProgress && <LinearProgress />}

                <Divider />
                {monitorData && !isEmpty(monitorData.pending_results) && (
                  <>
                    <Tabs
                      value={activeTab}
                      onChange={(_e, newValue) => setActiveTab(newValue)}
                      sx={{ px: 2 }}
                    >
                      <Tab label="Data Quality" />
                      <Tab label="Monitor" />
                    </Tabs>
                    <Divider />
                  </>
                )}
                <Container
                  style={{
                    minHeight: "400px",
                    maxHeight: "calc(100vh - 270px)",
                    overflowY: "scroll",
                  }}
                >
                  {activeTab === 0 && (
                    <TableContainer>
                      <Table size={"small"}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <span />
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant={"h5"}
                                style={{ fontWeight: "bold" }}
                              >
                                CRM
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant={"h5"}
                                style={{ fontWeight: "bold" }}
                              >
                                CIRROM
                              </Typography>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getRecordRows().map((entry) => {
                            return (
                              <TableRowForSuggestedChange
                                key={entry[0]}
                                fieldName={entry[0]}
                                fieldFromAccount={entry[2]}
                                integratedSystemId={integratedSystemId}
                                suggestedExplainEntry={entry[1]}
                                onSuccess={(suggestedId?) => {
                                  fetchSuggestedChangeExplain();
                                  props.onSuccess(suggestedId);
                                  setRequestInProgress(false);
                                }}
                                displayAlert={displayAlert}
                                suggestedChangeId={suggestedChangeId}
                                onActionInitiated={() => {
                                  setRequestInProgress(true);
                                }}
                              />
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                  {activeTab === 1 &&
                    monitorData &&
                    !isEmpty(monitorData.pending_results) && (
                      <MonitorComparisonTable
                        suggestedExplain={monitorData}
                        recordId={recordId || changeRecordId}
                        integratedSystemId={integratedSystemId}
                        requestInProgress={monitorRequestInProgress}
                        setRequestInProgress={setMonitorRequestInProgress}
                        onSuccess={(id?) => {
                          props.onSuccess(id);
                          if (
                            (recordId || changeRecordId) &&
                            props.integratedSystemId
                          ) {
                            setMonitorRequestInProgress(true);
                            getMonitorCRMComparison(
                              recordId || changeRecordId,
                              props.integratedSystemId
                            )
                              .then((resp) => setMonitorData(resp))
                              .catch(() => setMonitorData(undefined))
                              .finally(() =>
                                setMonitorRequestInProgress(false)
                              );
                          }
                        }}
                        displayAlert={displayAlert}
                      />
                    )}
                  {activeTab === 1 && monitorRequestInProgress && (
                    <LinearProgress />
                  )}
                </Container>

                <Container
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  {activeTab === 0 && (
                    <>
                      <div style={{ display: "flex", gap: "10px" }}>
                        <Tooltip
                          title="Share contact details via email with image export"
                          componentsProps={{
                            tooltip: { style: { background: "black" } },
                          }}
                        >
                          <IconButton
                            onClick={handleShareRecord}
                            color={"info"}
                            style={{
                              border: "1px solid black",
                              color: "black",
                              background: "#ffbf23",
                              height: "25px",
                              width: "25px",
                            }}
                          >
                            <Share
                              fontSize={"small"}
                              style={{ width: "16px", height: "16px" }}
                            />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title="Copy contact record image to clipboard"
                          componentsProps={{
                            tooltip: { style: { background: "black" } },
                          }}
                        >
                          <IconButton
                            onClick={handleCopyImageToClipboard}
                            color={"info"}
                            style={{
                              color: "black",
                              border: "1px solid black",
                              background: "#ffbf23",
                              height: "25px",
                              width: "25px",
                            }}
                          >
                            <ContentCopy
                              fontSize={"small"}
                              style={{ width: "16px", height: "16px" }}
                            />
                          </IconButton>
                        </Tooltip>
                        {!isFeedbackOpen && feedbackContext.canSupplyFeedback && (
                          <Tooltip
                            title={
                              <>
                                <Typography variant={"caption"}>
                                  Send feedback to Cirrom about this suggestion
                                </Typography>
                                <br />
                                <Typography variant={"caption"}>
                                  NOTE: No PII data will be included with your
                                  submission - please think carefully what data
                                  you choose to share.
                                </Typography>
                              </>
                            }
                            componentsProps={{
                              tooltip: { style: { background: "black" } },
                            }}
                          >
                            <IconButton
                              onClick={() => setIsFeedbackOpen(true)}
                              color={"info"}
                              style={{
                                border: "1px solid black",
                                color: "black",
                                background: "#ffbf23",
                                height: "25px",
                                width: "25px",
                              }}
                            >
                              <FeedbackOutlined
                                fontSize={"small"}
                                style={{ width: "16px", height: "16px" }}
                              />
                            </IconButton>
                          </Tooltip>
                        )}
                      </div>

                      {canApproveOrReject() && (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "row-reverse",
                            gap: "10px",
                            padding: "10px",
                          }}
                        >
                          <Button
                            size="small"
                            color="primary"
                            variant="contained"
                            onClick={() => approveAllSuggestedChanges(false)}
                          >
                            Approve All
                          </Button>
                          <Button
                            size="small"
                            color="secondary"
                            variant="contained"
                            onClick={() => approveAllSuggestedChanges(true)}
                          >
                            Reject All
                          </Button>
                        </div>
                      )}
                    </>
                  )}
                </Container>
                {isFeedbackOpen && (
                  <RecordDetailsFeedback
                    onClose={() => setIsFeedbackOpen(false)}
                  />
                )}
              </Card>
            </Zoom>
          ) : (
            <RecordDetailsNotReady onClick={() => props.onClose()} />
          )}
        </Container>
        {isCopilotOpen &&
          feedbackContext.canUseCopilot &&
          !!suggestedExplain &&
          !!integratedSystemId &&
          !!(recordId || changeRecordId || suggestedChangeId) && (
            <CopilotPanel
              integratedSystemId={integratedSystemId}
              recordId={recordId || changeRecordId}
              suggestedChangeId={suggestedChangeId}
              onClose={() => setIsCopilotOpen(false)}
            />
          )}
      </div>
    </Modal>
  );
}
